import React, { useEffect, useRef, useState } from "react";
import {
  ScanLine, Video, VideoOff, Loader2, BookPlus, Trash2, Eye, CheckCircle2, Pencil, Check, Sparkles, X, Moon, RefreshCw, AlertTriangle, Shield, ShieldOff, Target,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useCamera } from "@/hooks/useCamera";

function ResultCard({ result, lastImage, teachLabel, setTeachLabel, teaching, onTeach }) {
  return (
    <div className="bg-black/40 border border-white/10 rounded-xl p-4" data-testid="vision-result-card">
      <div className="flex items-center gap-2 mb-3">
        {result.recognized ? (
          <span
            className="text-[9px] font-mono-data uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-400/15 text-emerald-300 border border-emerald-400/30"
            data-testid="vision-recognized-badge"
          >
            Recognized · {Math.round((result.confidence || 0) * 100)}% match · sighted {result.sighting_count}x
          </span>
        ) : (
          <span
            className="text-[9px] font-mono-data uppercase tracking-wider px-2 py-1 rounded-full bg-amber-400/15 text-amber-300 border border-amber-400/30"
            data-testid="vision-unrecognized-badge"
          >
            Unconfirmed - best guess only
          </span>
        )}
      </div>
      <h3 className="font-display font-semibold text-white text-base capitalize mb-2" data-testid="vision-subject">
        {result.subject}
      </h3>
      <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap" data-testid="vision-description">
        {result.description}
      </p>
      {result.sources?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5 flex flex-wrap gap-x-3 gap-y-1.5">
          {result.sources.map((s, i) => (
            <a
              key={i}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`vision-source-${i}`}
              className="text-[9px] font-mono-data text-cyan-400/70 hover:text-cyan-300 truncate max-w-[200px] transition-colors"
            >
              [{i + 1}] {s.title}
            </a>
          ))}
        </div>
      )}
      {!result.recognized && (
        <div className="mt-4 pt-4 border-t border-white/5">
          <label className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 block mb-1.5">
            Teach AVA this object's name - she'll recognize it instantly next time
          </label>
          <div className="flex gap-2">
            <input
              value={teachLabel}
              onChange={(e) => setTeachLabel(e.target.value)}
              data-testid="teach-label-input"
              placeholder="e.g. coffee mug"
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-cyan-400/40 font-mono-data"
            />
            <button
              onClick={() => onTeach(lastImage)}
              disabled={teaching || !teachLabel.trim()}
              data-testid="teach-object-button"
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25 border border-cyan-400/30 text-xs font-display font-semibold uppercase tracking-wider transition-colors disabled:opacity-40 whitespace-nowrap"
            >
              <BookPlus className="w-3.5 h-3.5" /> {teaching ? "Learning..." : "Teach"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SelfLearnedRow({ o, onRename, onForget }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(o.label);
  return (
    <div className="flex items-center gap-2 bg-violet-400/5 border border-violet-400/20 rounded-lg pl-1.5 pr-1 py-1" data-testid={`self-learned-${o.id}`}>
      {o.photo_url && <img src={o.photo_url} alt={o.label} className="w-8 h-8 rounded object-cover border border-white/10" />}
      {editing ? (
        <>
          <input
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) { onRename(o.id, val); setEditing(false); } }}
            autoFocus
            data-testid={`rename-input-${o.id}`}
            className="w-32 bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-slate-200 outline-none focus:border-cyan-400/40 font-mono-data"
          />
          <button
            onClick={() => { if (val.trim()) { onRename(o.id, val); } setEditing(false); }}
            data-testid={`rename-save-${o.id}`}
            className="text-emerald-400 hover:text-emerald-300 p-1"
            title="Save name"
          >
            <Check className="w-3 h-3" />
          </button>
        </>
      ) : (
        <>
          <span className="text-[11px] text-slate-300 capitalize">{o.label}</span>
          <span className="text-[9px] text-slate-500 font-mono-data">×{o.sighting_count}</span>
          <button
            onClick={() => { setVal(o.label); setEditing(true); }}
            data-testid={`rename-object-${o.id}`}
            className="text-slate-500 hover:text-cyan-300 transition-colors p-1"
            title="Rename this object"
          >
            <Pencil className="w-3 h-3" />
          </button>
        </>
      )}
      <button
        onClick={() => onForget(o.id)}
        data-testid={`forget-object-${o.id}`}
        className="text-slate-500 hover:text-rose-400 transition-colors p-1"
        title="Forget this object"
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

function ReviewCard({ item, onApprove, onReject }) {
  const [label, setLabel] = useState(item.proposed_label);
  return (
    <div className="flex items-center gap-3 bg-amber-400/5 border border-amber-400/20 rounded-lg p-2" data-testid={`review-item-${item.id}`}>
      {item.thumbnail && <img src={item.thumbnail} alt={item.proposed_label} className="w-12 h-12 rounded object-cover border border-white/10" />}
      <div className="flex flex-col gap-1">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          data-testid={`review-label-${item.id}`}
          className="w-36 bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-slate-200 outline-none focus:border-cyan-400/40 font-mono-data"
        />
        <span className="text-[9px] font-mono-data text-amber-300/80">{Math.round((item.confidence || 0) * 100)}% · {item.detector}</span>
      </div>
      <div className="flex flex-col gap-1">
        <button
          onClick={() => label.trim() && onApprove(item.id, label)}
          data-testid={`review-approve-${item.id}`}
          className="p-1.5 rounded bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400/25 border border-emerald-400/30 transition-colors"
          title="Approve - AVA learns it under this name"
        >
          <Check className="w-3 h-3" />
        </button>
        <button
          onClick={() => onReject(item.id)}
          data-testid={`review-reject-${item.id}`}
          className="p-1.5 rounded bg-rose-400/10 text-rose-300 hover:bg-rose-400/20 border border-rose-400/30 transition-colors"
          title="Reject - retained as a hard-negative so AVA never falsely matches it"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// Highlighted card for high-confidence sightings where YOLO and Mask R-CNN disagree
// on WHAT the object is, OR where strict-gate is on. Doc's failsafe UI: two independent
// detectors both say ≥90% but call it different things - she never enrolls it silently,
// Doc picks the truth (or rejects both).
function DisagreementCard({ item, onApprove, onReject }) {
  const parts = (item.detector || "").split(":");
  const rival = parts.length > 1 ? parts[1] : null;
  const isGated = (item.detector || "").includes("·gated");
  const [pick, setPick] = useState(item.proposed_label);
  const choices = rival ? [item.proposed_label, rival] : [item.proposed_label];
  return (
    <div
      className="flex items-center gap-3 bg-rose-500/5 border-2 border-rose-400/30 rounded-xl p-3 shadow-[0_0_24px_rgba(244,63,94,0.15)]"
      data-testid={`disagreement-item-${item.id}`}
    >
      {item.thumbnail && (
        <img src={item.thumbnail} alt={item.proposed_label} className="w-16 h-16 rounded-lg object-cover border border-rose-400/40" />
      )}
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-300" />
          <span className="text-[10px] font-mono-data uppercase tracking-wider text-rose-300">
            {isGated ? "Gated · needs your verdict" : "Detectors disagree"}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {choices.map((label) => (
            <button
              key={label}
              onClick={() => setPick(label)}
              data-testid={`disagreement-choice-${item.id}-${label}`}
              className={`text-[11px] font-mono-data px-2 py-1 rounded border transition-colors ${
                pick === label
                  ? "bg-emerald-400/20 text-emerald-200 border-emerald-400/50"
                  : "bg-black/40 text-slate-300 border-white/10 hover:border-white/30"
              }`}
            >
              {label}
            </button>
          ))}
          <input
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            data-testid={`disagreement-input-${item.id}`}
            placeholder="or type the truth"
            className="w-28 bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-slate-200 outline-none focus:border-cyan-400/40 font-mono-data"
          />
        </div>
        <span className="text-[9px] font-mono-data text-slate-500">
          {Math.round((item.confidence || 0) * 100)}% · {item.detector}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <button
          onClick={() => pick.trim() && onApprove(item.id, pick.trim())}
          data-testid={`disagreement-approve-${item.id}`}
          className="p-1.5 rounded bg-emerald-400/15 text-emerald-300 hover:bg-emerald-400/25 border border-emerald-400/30 transition-colors"
          title="Commit - AVA learns the picked label"
        >
          <Check className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onReject(item.id)}
          data-testid={`disagreement-reject-${item.id}`}
          className="p-1.5 rounded bg-rose-400/10 text-rose-300 hover:bg-rose-400/20 border border-rose-400/30 transition-colors"
          title="Reject - nothing enters memory; retained as a hard-negative"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function CameraVision() {
  const { videoRef, active, error, facing, start, stop, capture, flip } = useCamera();
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [lastImage, setLastImage] = useState(null);
  const [teachLabel, setTeachLabel] = useState("");
  const [teaching, setTeaching] = useState(false);
  const [objects, setObjects] = useState([]);
  const [memories, setMemories] = useState([]);
  const [memCount, setMemCount] = useState(0);
  const [review, setReview] = useState({ pending: [], discovery: [], hard_negatives: 0 });
  const [detections, setDetections] = useState(null); // { items, w, h } - live YOLO HUD
  const [lastSleep, setLastSleep] = useState(null);
  const [sleeping, setSleeping] = useState(false);
  const [benchmark, setBenchmark] = useState(null);
  const [freezing, setFreezing] = useState(false);
  const [strictGate, setStrictGate] = useState(false);
  const [togglingGate, setTogglingGate] = useState(false);
  const detectBusyRef = useRef(false);

  const fetchObjects = () => api.get("/vision/objects").then((res) => setObjects(res.data)).catch(() => {});
  const fetchMemories = () =>
    api.get("/vision/memories?limit=48").then((res) => {
      setMemories(res.data.memories || []);
      setMemCount(res.data.count || 0);
    }).catch(() => {});

  const fetchReview = () => api.get("/vision/review").then((res) => setReview(res.data)).catch(() => {});
  const fetchGate = () => api.get("/vision/gate").then((res) => setStrictGate(!!res.data.strict)).catch(() => {});

  const toggleGate = async () => {
    setTogglingGate(true);
    try {
      const next = !strictGate;
      const res = await api.post("/vision/gate", { strict: next });
      setStrictGate(!!res.data.strict);
      toast.success(
        res.data.strict
          ? "Human Validation Gate ON - every new sighting will wait for your verdict."
          : "Human Validation Gate OFF - ≥90% confidence sightings auto-enroll again."
      );
    } catch {
      toast.error("Could not update the validation gate.");
    } finally {
      setTogglingGate(false);
    }
  };

  // Live detection HUD: while the camera runs, a YOLO pass every 2s draws what AVA's
  // first-stage cortex sees - no LLM involved, pure local detector.
  useEffect(() => {
    if (!active) {
      setDetections(null);
      return undefined;
    }
    const tick = () => {
      if (detectBusyRef.current) return;
      const image = capture();
      if (!image) return;
      detectBusyRef.current = true;
      api.post("/vision/detect", { image })
        .then((res) => setDetections({ items: res.data.detections || [], w: res.data.image_width, h: res.data.image_height }))
        .catch(() => {})
        .finally(() => { detectBusyRef.current = false; });
    };
    tick();
    const timer = setInterval(tick, 2000);
    return () => clearInterval(timer);
  }, [active, capture]);

  const fetchLastSleep = () => api.get("/vision/consolidate/last").then((res) => setLastSleep(res.data)).catch(() => {});
  const fetchBenchmark = () => api.get("/vision/benchmark").then((res) => setBenchmark(res.data)).catch(() => {});

  useEffect(() => {
    fetchObjects();
    fetchMemories();
    fetchReview();
    fetchLastSleep();
    fetchGate();
    fetchBenchmark();
  }, []);

  const handleSleepCycle = async () => {
    setSleeping(true);
    try {
      const r = (await api.post("/vision/consolidate")).data;
      toast.success(
        `Sleep cycle done - ${r.merges.length} merge${r.merges.length === 1 ? "" : "s"}, ${r.strengthened.length} strengthened, ${r.discovery_pruned} pruned` +
        (r.benchmark ? `, benchmark ${Math.round(r.benchmark.accuracy * 100)}%` : "") + "."
      );
      fetchObjects();
      fetchReview();
      fetchMemories();
      fetchLastSleep();
      fetchBenchmark();
    } catch {
      toast.error("Sleep cycle failed - try again.");
    } finally {
      setSleeping(false);
    }
  };

  const handleFreezeBenchmark = async () => {
    setFreezing(true);
    try {
      const r = (await api.post("/vision/benchmark/freeze")).data;
      toast.success(
        `Benchmark frozen - ${r.frozen} sightings across ${r.labels} objects` +
        (r.baseline ? `, baseline ${Math.round(r.baseline.accuracy * 100)}%` : "") + "."
      );
      fetchBenchmark();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Benchmark freeze failed - try again.");
    } finally {
      setFreezing(false);
    }
  };

  const handleScan = async () => {
    const image = capture();
    if (!image) {
      toast.error("Camera isn't active yet.");
      return;
    }
    setLastImage(image);
    setScanning(true);
    setResult(null);
    // Every manual scan also passes through AVA's 512-d visual episodic memory
    // (reinforce-or-commit) - silent, fire-and-forget, independent of the describe call.
    api.post("/vision/observe", { image, source: "vision-tab" }).then(fetchMemories).catch(() => {});
    try {
      const res = await api.post("/vision/describe", { image });
      setResult(res.data);
      setTeachLabel(res.data.suggested_label || "");
      if (res.data.recognized) fetchObjects();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AVA's visual cortex couldn't process that frame.");
    } finally {
      setScanning(false);
    }
  };

  const handleTeach = async (image) => {
    if (!image || !teachLabel.trim()) return;
    setTeaching(true);
    try {
      const res = await api.post("/vision/teach", { image, label: teachLabel.trim() });
      toast.success(`AVA will recognize "${res.data.label}" instantly next time.`);
      setResult((prev) => (prev ? { ...prev, recognized: true, subject: res.data.label, confidence: 1, sighting_count: res.data.sighting_count } : prev));
      fetchObjects();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Teaching failed.");
    } finally {
      setTeaching(false);
    }
  };

  const handleForget = async (id) => {
    await api.delete(`/vision/objects/${id}`);
    setObjects((prev) => prev.filter((o) => o.id !== id));
  };

  const handleRename = async (id, label) => {
    try {
      const res = await api.patch(`/vision/objects/${id}`, { label: label.trim() });
      toast.success(`Renamed to "${res.data.label}".`);
      fetchObjects();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed.");
    }
  };

  const handleApprove = async (id, label) => {
    try {
      const res = await api.post(`/vision/review/${id}/approve`, { label });
      toast.success(`AVA learned "${res.data.label}" - approved from the review queue.`);
      fetchReview();
      fetchObjects();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed.");
    }
  };

  const handleReject = async (id) => {
    try {
      await api.post(`/vision/review/${id}/reject`);
      toast.success("Rejected - retained as a hard-negative so AVA never falsely matches it.");
      fetchReview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed.");
    }
  };

  const handleDeleteReview = async (id) => {
    await api.delete(`/vision/review/${id}`).catch(() => {});
    fetchReview();
  };

  const handleForgetMemory = async (id) => {
    await api.delete(`/vision/memories/${id}`).catch(() => {});
    setMemories((prev) => prev.filter((m) => m.id !== id));
    setMemCount((c) => Math.max(0, c - 1));
  };

  const selfLearned = objects.filter((o) => o.self_taught);
  const docTaught = objects.filter((o) => !o.self_taught);

  return (
    <div className="col-span-1 md:col-span-10 h-full glass-panel rounded-2xl p-4 sm:p-6 overflow-y-auto" data-testid="camera-vision-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold text-white text-lg">Camera Vision</h2>
          <p className="text-[11px] text-slate-500 mt-1">
            Frozen CLIP embeddings + few-shot learning-to-learn · {objects.length} object{objects.length === 1 ? "" : "s"} in AVA's trained visual memory
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div
            className="relative aspect-video bg-black/60 rounded-xl border border-white/10 overflow-hidden flex items-center justify-center"
            data-testid="camera-preview-container"
          >
            {/* Always mounted (hidden via CSS, not conditionally rendered) so `videoRef`
                already points at a real DOM node the instant `start()` resolves - mounting
                it only when `active` is true left the very first getUserMedia() stream with
                nowhere to attach, so the preview stayed blank even though the camera was on. */}
            <video ref={videoRef} className={`w-full h-full object-cover ${active ? "" : "hidden"}`} muted playsInline />
            {!active && (
              <div className="text-center text-slate-500 text-xs px-6 flex flex-col items-center gap-2">
                <Eye className="w-6 h-6 text-slate-600" />
                {error || "Camera is off. Start it to point AVA's visual cortex at something."}
              </div>
            )}
            {scanning && (
              <div className="absolute inset-0 bg-cyan-400/10 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-cyan-300 animate-spin" />
              </div>
            )}

            {/* Live YOLO HUD - preserveAspectRatio "slice" mirrors the video's object-cover
                cropping exactly, so boxes in raw frame pixels land on the right spots. */}
            {active && detections && detections.w > 0 && (
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none"
                viewBox={`0 0 ${detections.w} ${detections.h}`}
                preserveAspectRatio="xMidYMid slice"
                data-testid="detection-overlay"
              >
                {detections.items.map((d, i) => {
                  const [x1, y1, x2, y2] = d.box;
                  const c = d.confidence >= 0.9 ? "#34d399" : d.confidence >= 0.5 ? "#fbbf24" : "#94a3b8";
                  const fs = Math.max(14, detections.w / 42);
                  return (
                    <g key={i}>
                      <rect
                        x={x1} y={y1} width={x2 - x1} height={y2 - y1} rx="6"
                        fill="none" stroke={c} strokeWidth={Math.max(2, detections.w / 350)} opacity="0.9"
                      />
                      <text
                        x={x1 + 5} y={y1 < fs + 10 ? y1 + fs + 4 : y1 - 7}
                        fill={c} fontSize={fs} fontFamily="JetBrains Mono, monospace"
                        style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.85)", strokeWidth: 4 }}
                      >
                        {d.label} {Math.round(d.confidence * 100)}%
                      </text>
                    </g>
                  );
                })}
              </svg>
            )}
            {active && detections && (
              <span
                className="absolute top-2 right-2 text-[8px] font-mono-data uppercase tracking-wider text-cyan-300/80 bg-black/60 border border-cyan-400/20 rounded-full px-2 py-0.5"
                data-testid="detection-count-badge"
              >
                YOLO · {detections.items.length} object{detections.items.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {!active ? (
              <button
                onClick={start}
                data-testid="start-camera-button"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 text-[11px] font-mono-data uppercase tracking-wider transition-colors"
              >
                <Video className="w-3.5 h-3.5" /> Start Camera
              </button>
            ) : (
              <>
                <button
                  onClick={handleScan}
                  disabled={scanning}
                  data-testid="scan-object-button"
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-400/15 hover:bg-cyan-400/25 border border-cyan-400/30 text-cyan-300 text-[11px] font-mono-data uppercase tracking-wider transition-colors disabled:opacity-50"
                >
                  {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />}
                  {scanning ? "Analyzing..." : "Scan Object"}
                </button>
                <button
                  onClick={flip}
                  data-testid="flip-camera-button"
                  title={`Switch to ${facing === "environment" ? "front" : "rear"} camera`}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 text-[11px] font-mono-data uppercase tracking-wider transition-colors"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">{facing === "environment" ? "Rear" : "Front"}</span>
                </button>
                <button
                  onClick={stop}
                  data-testid="stop-camera-button"
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 text-[11px] font-mono-data uppercase tracking-wider transition-colors"
                >
                  <VideoOff className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        </div>

        <div>
          {result ? (
            <ResultCard
              result={result}
              lastImage={lastImage}
              teachLabel={teachLabel}
              setTeachLabel={setTeachLabel}
              teaching={teaching}
              onTeach={handleTeach}
            />
          ) : (
            <div
              className="h-full min-h-[180px] flex items-center justify-center text-center text-slate-500 text-xs p-8 border border-dashed border-white/10 rounded-xl"
              data-testid="vision-empty-state"
            >
              Start the camera and scan an object - AVA checks her trained visual memory first, then reasons about anything new.
            </div>
          )}
        </div>
      </div>

      {selfLearned.length > 0 && (
        <div className="mt-6 pt-6 border-t border-white/5" data-testid="self-learned-section">
          <h3 className="text-[10px] font-mono-data uppercase tracking-wider text-violet-300/80 mb-3 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Learned On My Own ({selfLearned.length}) - objects AVA identified and memorized autonomously
          </h3>
          <div className="flex flex-wrap gap-2">
            {selfLearned.map((o) => (
              <SelfLearnedRow key={o.id} o={o} onRename={handleRename} onForget={handleForget} />
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 pt-6 border-t border-white/5">
        <h3 className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500 mb-3">
          Taught By Doc ({docTaught.length})
        </h3>
        {docTaught.length === 0 ? (
          <p className="text-[11px] text-slate-600" data-testid="no-learned-objects">Nothing taught yet - scan and teach AVA her first object.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {docTaught.map((o) => (
              <div
                key={o.id}
                data-testid={`learned-object-${o.id}`}
                className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full pl-3 pr-1.5 py-1"
              >
                <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                <span className="text-[11px] text-slate-300 capitalize">{o.label}</span>
                <span className="text-[9px] text-slate-500 font-mono-data">×{o.sighting_count}</span>
                <button
                  onClick={() => handleForget(o.id)}
                  data-testid={`forget-object-${o.id}`}
                  className="text-slate-500 hover:text-rose-400 transition-colors p-1"
                  title="Forget this object"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 pt-6 border-t border-white/5" data-testid="review-queue-section">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h3 className="text-[10px] font-mono-data uppercase tracking-wider text-amber-300/80">
            Review Queue ({review.pending.length}) - 50-90% confidence sightings awaiting your verdict
            {review.hard_negatives > 0 ? ` · ${review.hard_negatives} hard-negative${review.hard_negatives === 1 ? "" : "s"} retained` : ""}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleGate}
              disabled={togglingGate}
              data-testid="validation-gate-toggle"
              title={
                strictGate
                  ? "STRICT - every new sighting (even ≥90%) waits for your approval before entering permanent memory"
                  : "PERMISSIVE - ≥90% confidence sightings auto-enroll; only 50-90% wait for your review"
              }
              className={`flex items-center gap-1.5 text-[9px] font-mono-data uppercase tracking-wider rounded-full px-3 py-1 border transition-colors disabled:opacity-50 ${
                strictGate
                  ? "text-rose-300 bg-rose-500/15 hover:bg-rose-500/25 border-rose-400/40"
                  : "text-slate-400 bg-white/5 hover:bg-white/10 border-white/10"
              }`}
            >
              {togglingGate ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : strictGate ? (
                <Shield className="w-3 h-3" />
              ) : (
                <ShieldOff className="w-3 h-3" />
              )}
              Human Gate {strictGate ? "ON" : "OFF"}
            </button>
            {lastSleep?.ran_at && (
              <span className="text-[9px] font-mono-data text-slate-600" data-testid="last-sleep-cycle">
                last sleep cycle {String(lastSleep.ran_at).slice(0, 16).replace("T", " ")} UTC
              </span>
            )}
            <button
              onClick={handleSleepCycle}
              disabled={sleeping}
              data-testid="run-sleep-cycle-button"
              className="flex items-center gap-1.5 text-[9px] font-mono-data uppercase tracking-wider text-violet-300 bg-violet-400/10 hover:bg-violet-400/20 border border-violet-400/30 rounded-full px-3 py-1 transition-colors disabled:opacity-50"
              title="Merge duplicate prototypes, strengthen frequent objects, prune stale discovery samples (auto-runs nightly at 03:00 UTC)"
            >
              {sleeping ? <Loader2 className="w-3 h-3 animate-spin" /> : <Moon className="w-3 h-3" />}
              Sleep cycle
            </button>
            {benchmark?.latest && (
              <span
                className="text-[9px] font-mono-data uppercase tracking-wider text-emerald-300/90 bg-emerald-400/10 border border-emerald-400/25 rounded-full px-3 py-1"
                data-testid="benchmark-score-chip"
                title={`Frozen benchmark of ${benchmark.set_size} sightings - re-scored during every nightly sleep cycle so consolidation never silently degrades recognition. Last run ${String(benchmark.latest.ran_at).slice(0, 16).replace("T", " ")} UTC.`}
              >
                benchmark {Math.round(benchmark.latest.accuracy * 100)}% ({benchmark.latest.correct}/{benchmark.latest.total})
              </span>
            )}
            <button
              onClick={handleFreezeBenchmark}
              disabled={freezing}
              data-testid="freeze-benchmark-button"
              className="flex items-center gap-1.5 text-[9px] font-mono-data uppercase tracking-wider text-emerald-300 bg-emerald-400/10 hover:bg-emerald-400/20 border border-emerald-400/30 rounded-full px-3 py-1 transition-colors disabled:opacity-50"
              title="Freeze the current approved sightings as a fixed benchmark set - AVA is scored against it during every nightly sleep cycle"
            >
              {freezing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Target className="w-3 h-3" />}
              Freeze benchmark
            </button>
          </div>
        </div>
        {(() => {
          const isDisagreement = (item) => (item.detector || "").includes("≠") || (item.detector || "").includes("·gated");
          const disagreements = review.pending.filter(isDisagreement);
          const regular = review.pending.filter((i) => !isDisagreement(i));
          return (
            <>
              {disagreements.length > 0 && (
                <div className="mb-4" data-testid="disagreement-alerts-section">
                  <h4 className="text-[9px] font-mono-data uppercase tracking-wider text-rose-300/90 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="w-3 h-3" />
                    Disagreement Alerts ({disagreements.length}) - two detectors saw the same thing but called it different things (or strict-gated)
                  </h4>
                  {/* Vertical stack with reserved right-padding at all breakpoints
                      so the fixed bottom-right voice orb never overlaps the card's
                      approve/reject action column - clicks always land cleanly. */}
                  <div className="flex flex-col gap-2 sm:pr-24 lg:pr-28">
                    {disagreements.map((item) => (
                      <DisagreementCard key={item.id} item={item} onApprove={handleApprove} onReject={handleReject} />
                    ))}
                  </div>
                </div>
              )}
              {regular.length === 0 && disagreements.length === 0 ? (
                <p className="text-[11px] text-slate-600" data-testid="review-queue-empty">
                  Empty - sightings AVA is 50-90% sure about wait here for your approve/reject; ≥90% enroll automatically
                  {strictGate ? " (currently gated - even ≥90% will queue here)" : ""}; &lt;50% land in discovery below.
                </p>
              ) : (
                regular.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {regular.map((item) => (
                      <ReviewCard key={item.id} item={item} onApprove={handleApprove} onReject={handleReject} />
                    ))}
                  </div>
                )
              )}
            </>
          );
        })()}
        {review.discovery.length > 0 && (
          <div className="mt-4">
            <h4 className="text-[9px] font-mono-data uppercase tracking-wider text-slate-600 mb-2">
              Discovery samples - &lt;50% confidence glimpses retained for future learning (rejected items live here as hard-negatives)
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {review.discovery.map((d) => (
                <div
                  key={d.id}
                  className={`group relative rounded overflow-hidden border ${d.status === "rejected" ? "border-rose-400/40" : "border-white/10"}`}
                  title={`${d.proposed_label} · ${Math.round((d.confidence || 0) * 100)}%${d.status === "rejected" ? " · rejected (hard-negative)" : ""}`}
                  data-testid={`discovery-item-${d.id}`}
                >
                  {d.thumbnail ? (
                    <img src={d.thumbnail} alt={d.proposed_label} className="w-10 h-10 object-cover" />
                  ) : (
                    <div className="w-10 h-10 bg-black/40" />
                  )}
                  <button
                    onClick={() => handleDeleteReview(d.id)}
                    className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-black/70 text-rose-400"
                    data-testid={`discovery-delete-${d.id}`}
                    title="Delete this sample"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 pt-6 border-t border-white/5" data-testid="visual-memory-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[10px] font-mono-data uppercase tracking-wider text-slate-500">
            Visual Episodic Memory - everything AVA has seen
          </h3>
          <span className="text-[9px] font-mono-data text-cyan-400/70" data-testid="visual-memory-count">
            {memCount} sighting{memCount === 1 ? "" : "s"} · 512-d feature vectors
          </span>
        </div>
        {memories.length === 0 ? (
          <p className="text-[11px] text-slate-600" data-testid="no-visual-memories">
            Nothing witnessed yet - AVA automatically embeds every scene from Live Vision and manual scans
            into her 512-dimensional vector memory, and recalls repeat sightings.
          </p>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
            {memories.map((m) => (
              <div
                key={m.id}
                data-testid={`memory-item-${m.id}`}
                className={`group relative rounded-lg overflow-hidden border bg-black/40 ${m.snapshot ? "border-emerald-400/50" : "border-white/10"}`}
                title={`${m.label || "unlabeled sighting"} · seen ${m.times_seen}x · via ${m.source || "live-vision"}${m.snapshot ? " · rare-sighting snapshot" : ""}`}
              >
                {m.thumbnail ? (
                  <img src={m.thumbnail} alt={m.label || "sighting"} className="w-full aspect-square object-cover" />
                ) : (
                  <div className="w-full aspect-square" />
                )}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-1.5 pt-4 pb-1">
                  <p className="text-[8px] font-mono-data text-slate-300 capitalize truncate">{m.label || "unlabeled"}</p>
                  <p className="text-[7px] font-mono-data text-slate-500">×{m.times_seen}</p>
                </div>
                <button
                  onClick={() => handleForgetMemory(m.id)}
                  data-testid={`forget-memory-${m.id}`}
                  className="absolute top-1 right-1 p-1 rounded bg-black/60 text-slate-400 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Forget this sighting"
                >
                  <Trash2 className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
